import { useCallback, useEffect, useMemo, useState } from 'react';
import { lastMoveUciAtPly } from 'react-chess-core';
import { fenAtPly, REPLAY_START_FEN } from 'react-chess-replay-trainer';

/** Scrub / auto-play navigation over a fixed move list (no async game fetch). */
export function useGameLineBrowser(
  movesUci: string[],
  resetKey: string,
  startFen: string = REPLAY_START_FEN,
) {
  const [plyIndex, setPlyIndex] = useState(0);
  const totalPly = movesUci.length;
  const complete = plyIndex >= totalPly && totalPly > 0;

  useEffect(() => {
    setPlyIndex(0);
  }, [resetKey, startFen]);

  const displayFen = useMemo(
    () => fenAtPly(movesUci, plyIndex),
    [movesUci, plyIndex],
  );

  const lastMoveUci = useMemo(
    () => lastMoveUciAtPly(movesUci, plyIndex),
    [movesUci, plyIndex],
  );

  const goTo = useCallback((ply: number) => {
    setPlyIndex(Math.max(0, Math.min(ply, totalPly)));
  }, [totalPly]);

  const goFirst = useCallback(() => {
    setPlyIndex(0);
  }, []);

  const goPrev = useCallback(() => {
    setPlyIndex((ply) => Math.max(0, ply - 1));
  }, []);

  const goNext = useCallback(() => {
    setPlyIndex((ply) => Math.min(totalPly, ply + 1));
  }, [totalPly]);

  const goLast = useCallback(() => {
    setPlyIndex(totalPly);
  }, [totalPly]);
  const stopTraining = useCallback(() => {}, []);

  return {
    plyIndex,
    totalPly,
    complete,
    displayFen,
    lastMoveUci,
    canPrev: plyIndex > 0,
    canNext: plyIndex < totalPly,
    goTo,
    goFirst,
    goPrev,
    goNext,
    goLast,
    stopTraining,
  };
}
